import { createSlice } from "@reduxjs/toolkit"
import { createAppAsyncThunk } from "app/hooks"
import { resetStoreAction, DEFAULT_SUGGESTED_MEALS_PAGE_SIZE } from "config"
import favoriteMealsAPI from "./favoriteMealsAPI"
import { IFavoriteMealsState } from "./favoriteMealsSlice.types"
import {
  ICreateMealPayload,
  IUpdateMealPayload,
} from "model/food/types/mealPayload.types"

let getFavoriteMealsAbortController: AbortController | null = null

export const getFavoriteMeals = createAppAsyncThunk(
  "favoriteMeals/getFavoriteMeals",
  async ({ page }: { page: number }, { rejectWithValue }) => {
    const isFirstPage = page === 1

    if (getFavoriteMealsAbortController && isFirstPage) {
      getFavoriteMealsAbortController.abort()
    }

    const localController = new AbortController()
    getFavoriteMealsAbortController = localController

    try {
      const { favoriteMeals, totalPages } =
        await favoriteMealsAPI.getFavoriteMeals({
          page,
          signal: localController.signal,
        })
      return { favoriteMeals, hasMorePage: page < totalPages }
    } catch (err: any) {
      if (localController.signal.aborted) {
        return rejectWithValue({ aborted: true })
      }
      return rejectWithValue(err)
    } finally {
      if (getFavoriteMealsAbortController === localController) {
        getFavoriteMealsAbortController = null
      }
    }
  },
)

export const getFavoriteMealDetails = createAppAsyncThunk(
  "favoriteMeals/getFavoriteMealDetails",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await favoriteMealsAPI.getFavoriteMealDetails(id)
      return response
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

export const createFavoriteMeal = createAppAsyncThunk(
  "favoriteMeals/createFavoriteMeal",
  async (payload: ICreateMealPayload, { rejectWithValue, dispatch }) => {
    try {
      const response = await favoriteMealsAPI.createFavoriteMeal(payload)
      dispatch(resetFavoriteMealsState())
      return response
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

export const updateFavoriteMeal = createAppAsyncThunk(
  "favoriteMeals/updateFavoriteMeal",
  async (payload: IUpdateMealPayload, { rejectWithValue, dispatch }) => {
    try {
      const response = await favoriteMealsAPI.updateFavoriteMeal(payload)
      dispatch(resetFavoriteMealsState())
      return response
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

export const deleteFavoriteMeal = createAppAsyncThunk(
  "favoriteMeals/deleteFavoriteMeal",
  async (id: string, { rejectWithValue, dispatch }) => {
    try {
      await favoriteMealsAPI.deleteFavoriteMeal(id)
      dispatch(resetFavoriteMealsState())
      return id
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

const initialState: IFavoriteMealsState = {
  favoriteMeals: [],
  getFavoriteMealsLoading: false,
  getFavoriteMealsFailed: undefined,
  isNoFavoriteMeals: false,
  favoriteMealsPage: 0,
  hasMoreFavoriteMeals: true,
  favoriteMealsScrollTop: 0,

  favoriteMealDetails: undefined,
  getFavoriteMealDetailsLoading: false,
  getFavoriteMealDetailsFailed: undefined,

  createFavoriteMealLoading: false,
  createFavoriteMealSuccess: undefined,
  createFavoriteMealFailed: undefined,

  updateFavoriteMealLoading: false,
  updateFavoriteMealSuccess: undefined,
  updateFavoriteMealFailed: undefined,

  deleteFavoriteMealLoading: false,
  deleteFavoriteMealSuccess: undefined,
  deleteFavoriteMealFailed: undefined,
}

export const favoriteMealsSlice = createSlice({
  name: "favoriteMeals",
  initialState,
  reducers: {
    resetFavoriteMeals(state) {
      state.favoriteMeals = []
      state.getFavoriteMealsLoading = false
      state.getFavoriteMealsFailed = undefined
      state.favoriteMealsPage = 0
      state.hasMoreFavoriteMeals = true
      state.favoriteMealsScrollTop = 0
    },

    setFavoriteMealDetails(state, { payload }) {
      state.favoriteMealDetails = payload
    },

    setFavoriteMealsScrollTop(state, { payload }) {
      state.favoriteMealsScrollTop = payload
    },

    resetFavoriteMealsState() {
      return initialState
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getFavoriteMeals.pending, (state, { meta }) => {
        state.getFavoriteMealsLoading = true
        state.favoriteMealsPage = meta?.arg?.page || 0
      })
      .addCase(
        getFavoriteMeals.fulfilled,
        (state, { payload: { favoriteMeals, hasMorePage } }) => {
          state.getFavoriteMealsLoading = false

          const isFirstPage = state.favoriteMealsPage === 1
          const currentMeals = isFirstPage ? [] : state.favoriteMeals

          state.favoriteMeals = [...currentMeals, ...favoriteMeals]
          state.isNoFavoriteMeals = state.favoriteMeals.length === 0
          state.hasMoreFavoriteMeals = hasMorePage
        },
      )
      .addCase(getFavoriteMeals.rejected, (state, { payload }) => {
        if ((payload as any)?.aborted) return
        state.getFavoriteMealsLoading = false
        state.getFavoriteMealsFailed = payload
      })

      .addCase(getFavoriteMealDetails.pending, (state) => {
        state.getFavoriteMealDetailsLoading = true
      })
      .addCase(getFavoriteMealDetails.fulfilled, (state, { payload }) => {
        state.getFavoriteMealDetailsLoading = false
        state.favoriteMealDetails = payload
      })
      .addCase(getFavoriteMealDetails.rejected, (state, { payload }) => {
        state.getFavoriteMealDetailsLoading = false
        state.getFavoriteMealDetailsFailed = payload
      })

      .addCase(createFavoriteMeal.pending, (state) => {
        state.createFavoriteMealLoading = true
      })
      .addCase(createFavoriteMeal.fulfilled, (state, { payload }) => {
        state.createFavoriteMealLoading = false
        state.createFavoriteMealSuccess = payload
        state.favoriteMeals = [payload, ...state.favoriteMeals]
      })
      .addCase(createFavoriteMeal.rejected, (state, { payload }) => {
        state.createFavoriteMealLoading = false
        state.createFavoriteMealFailed = payload
      })

      .addCase(updateFavoriteMeal.pending, (state) => {
        state.updateFavoriteMealLoading = true
      })
      .addCase(updateFavoriteMeal.fulfilled, (state, { payload }) => {
        state.updateFavoriteMealLoading = false
        state.updateFavoriteMealSuccess = payload
        state.favoriteMealDetails = payload
      })
      .addCase(updateFavoriteMeal.rejected, (state, { payload }) => {
        state.updateFavoriteMealLoading = false
        state.updateFavoriteMealFailed = payload
      })

      .addCase(deleteFavoriteMeal.pending, (state) => {
        state.deleteFavoriteMealLoading = true
      })
      .addCase(deleteFavoriteMeal.fulfilled, (state, { payload }) => {
        state.deleteFavoriteMealLoading = false
        state.deleteFavoriteMealSuccess = payload
        state.favoriteMeals = state.favoriteMeals.filter(
          (meal) => meal.id !== payload,
        )
      })
      .addCase(deleteFavoriteMeal.rejected, (state, { payload }) => {
        state.deleteFavoriteMealLoading = false
        state.deleteFavoriteMealFailed = payload
      })

      .addCase(resetStoreAction, () => {
        return initialState
      })
  },
})

export const {
  resetFavoriteMeals,
  setFavoriteMealDetails,
  setFavoriteMealsScrollTop,
  resetFavoriteMealsState,
} = favoriteMealsSlice.actions

export default favoriteMealsSlice.reducer
